/**
 * BhiGettingStartedGuide.jsx — iter742 (Feb 2026)
 *
 * Reusable "За нови пациенти — откъде да започна" guide.
 *
 * Renders the 30-day step-by-step orientation for new BHI users. Used in
 * two contexts:
 *   1. Inline block inside the /methodology/breast public page.
 *   2. Modal-triggered from the "🧭 Откъде да започна" button on the
 *      main BHI page — patients can consult it at any point without
 *      leaving the module.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, MapPin, User, HeartPulse, Utensils, FlaskConical, Layers, AlertTriangle, CheckCircle2, Circle, Loader2, Brain, Sparkles, Target, Bell, ShieldCheck } from "lucide-react";
import { useAuth } from "@/App";

const STEPS = [
  {
    id: "profile",
    day: "Ден 1 · 5 минути",
    label: "Профил",
    accent: "emerald",
    icon: User,
    tasks: [
      "Регистрация и вход",
      "Профил → дата на раждане (не само възраст), пол, ръст и тегло",
      "Мерки → талия и ханш",
      "Медицинска история → хормонални събития (менархе, бременности, менопауза)",
    ],
    note: "Ако сега отворите модула — ще видите „Данни в очакване“. Това е правилно, не грешка.",
  },
  {
    id: "base_assessment",
    day: "Ден 1 · още 10 минути",
    label: "Базова оценка (DKG + Health Assessment)",
    accent: "blue",
    icon: HeartPulse,
    tasks: [
      "DKG наследствен риск (12 въпроса за роднини с рак)",
      "Health Assessment → сън, стрес, движение, алкохол, пушене",
      "42 модифицируеми рискови фактора → отбележете тези, които ви се отнасят",
    ],
    result: "6 тъканни среди активни с реални стойности. Метаболитният профил още „в очакване“.",
    pathways_activated: [
      "NF-κB (възпаление) — активиран от стрес и качество на съня",
      "Циркадни ритми (BMAL1/CLOCK) — модулират хормоналните пикове",
      "DNA repair (при DKG+ анамнеза) — приоритет за проверка",
    ],
  },
  {
    id: "auto_diagnosis",
    day: "Ден 1 · веднага след профил",
    label: "🩻 Автодиагноза — какво платформата „вижда“ за теб",
    accent: "amber",
    icon: Layers,
    tasks: [
      "Отвори „Автоматична диагноза“ в BHI",
      "Прегледай списъка с автоматично разпознати състояния (PCOS, инсулинова резистентност, дефицити и др.)",
      "Отбележи потвърдени, неясни или подозрителни → системата ще фокусира пътищата",
    ],
    result: "Всяко потвърдено състояние отключва набор от KEGG пътища и насочени препоръки.",
    pathways_activated: [
      "PI3K-AKT-mTOR (инсулинова резистентност) — активиран при PCOS/IR",
      "Естроген сигнализация (ESR1/ESR2) — при хормонални нарушения",
      "Мастна тъкан (адипогенеза) — при метаболитен синдром",
    ],
  },
  {
    id: "daily_habits",
    day: "Ден 1 → всеки ден",
    label: "Живият метаболизъм (ежедневни навици)",
    accent: "green",
    icon: Sparkles,
    tasks: [
      "В BHI отворете „⚡ Ежедневни навици“ → 1-клик отметки за днес",
      "Крестоцветни, DIM, Ω-3, Сулфорафан, фибри, полифеноли",
      "Стил на живот: фастинг, сън, движение, тренировка, медитация",
      "Priority Action Card горе показва днешния приоритет",
    ],
    result: "KEGG пътищата се модулират в реално време при всяка отметка.",
    pathways_activated: [
      "AhR/Nrf2 (детоксикация) — активиран от крестоцветни, сулфорафан",
      "Естроболом (SHBG баланс) — фибри ≥25g подобряват",
      "Ω-3/възпаление — riba/добавка потушава NF-κB каскадата",
    ],
  },
  {
    id: "diary",
    day: "Ден 2 → 14",
    label: "Хранителен дневник (14-дневна средна)",
    accent: "purple",
    icon: Utensils,
    tasks: [
      "Записвайте 3–4 хранения дневно в Симулатора на чинията",
      "След 14 дни се формират средни NIS и DIL",
      "Автоматично интегриране с Precision Health & Nutrition Lab",
    ],
    result: "Активират се Инсулинов и IGF-1 индекс.",
    warning:
      "Едно хранене прави ~1/56 разлика в 14-дневната средна. Модулът НЕ реагира едномоментно — реалната обратна връзка се вижда след 3–7 дни последователна практика.",
    pathways_activated: [
      "Инсулин/IGF-1/mTOR — модулира клетъчния растеж",
      "PPAR-γ (мастни клетки) — реагира на макронутриентния профил",
      "Микробиом → SCFA → чревна бариера",
    ],
  },
  {
    id: "labs",
    day: "Ден 7 · ⭐ Ключова стъпка",
    label: "🧪 Лабораторни изследвания — най-важната обективна опорна точка",
    accent: "pink",
    icon: FlaskConical,
    tasks: [
      "Метаболитен панел: hsCRP, HOMA-IR (или fasting инсулин + глюкоза), витамин D",
      "Хормонален панел (жени): естрадиол, прогестерон, SHBG, DHEA-S, ФСХ (при менопауза)",
      "Липиден профил + феритин (по избор)",
      "Качете PDF в „Анализи (OCR)“ → системата извлича стойностите автоматично",
    ],
    result: "Активират се Възпалителен и Естрогенен индекс. Точността на BHI скача с ~40%.",
    highlight:
      "🔬 БЕЗ ТАЗИ СТЪПКА модулът работи предимно с въпросници. С лаборатории пътищата се захранват с ТВОИ реални стойности — не със средно-статистически.",
    pathways_activated: [
      "Естроген каскада (aromatase / CYP1A1 / CYP1B1) — от Estradiol/SHBG",
      "NF-κB възпаление — от hs-CRP",
      "Инсулин/mTOR — от HOMA-IR + fasting insulin",
      "Витамин D / VDR — регулира 200+ гена",
      "IGF-1 → PI3K-AKT — клетъчен растеж и деление",
    ],
  },
  {
    id: "twins",
    day: "Ден 7 → 14",
    label: "20-те дигитални близнака",
    accent: "indigo",
    icon: Sparkles,
    tasks: [
      "Разгледайте карусела с 20 дигитални здравни модела",
      "Всеки показва как ще изглежда тялото ви при различни сценарии",
      "Естроген, инсулин, щитовидна жлеза, витамин D, желязо, кортизол и др.",
    ],
    result: "Виждате конкретно кои фактори модулират кой близнак.",
  },
  {
    id: "mind_body",
    day: "Ден 14 → 30",
    label: "Mind & Body Lab",
    accent: "amber",
    icon: Brain,
    tasks: [
      "5-минутен дневен check-in: настроение, тревожност, енергия",
      "Дихателни техники (4-7-8) → 5 мин преди сън",
      "Стрес натоварване се вписва в BHI композитния скор",
    ],
    result: "Стрес-BHI мост се активира; циркадният ритъм влиза в модела.",
  },
  {
    id: "pathway_vault",
    day: "Ден 14 → 30",
    label: "Pathway Vault (16 активни пътища)",
    accent: "teal",
    icon: Target,
    tasks: [
      "Активирайте персонализирани приоритетни пътища (PI3K-AKT, ESR1, BRCA, MAPK…)",
      "Всеки pathway има препоръки, супплементи, мониторинг метрики",
      "Използвайте в комбинация с д-р Инков за клиничен контекст",
    ],
    result: "Персонализирани дългосрочни цели вместо общи препоръки.",
  },
  {
    id: "prevention_program",
    day: "Ден 30 → 90 → 365",
    label: "12-месечна Prevention Program",
    accent: "rose",
    icon: ShieldCheck,
    tasks: [
      "Запишете се в 12-месечната програма за подкрепа",
      "5 приоритетни теми, седмичен план, месечни прегледи",
      "Coach nudges + напомняния в „Действия за проследяване“",
    ],
    result: "Прогресивна редукция на метаболитно-инсулинов, възпалителен и чернодробно-чернодробно натоварване.",
  },
  {
    id: "depth",
    day: "Ден 30+ (опционално)",
    label: "Дълбочина — Мамография / DUTCH / Raw DNA",
    accent: "purple",
    icon: Layers,
    tasks: [
      "Ако имате мамография — въведете BI-RADS плътност ръчно в „Моите данни“",
      "(Опция) DUTCH тест → 2-OHE1 / 4-OHE1 / 16-OHE1 — прецизен естрогенен bridge",
      "(Опция) Raw DNA файл (23andMe / AncestryDNA) → генетичен профил",
      "(Опция) Геномна нутригенетика чрез Geno Lab",
    ],
    result: "Пълна дълбочина + trend graph + „📸 Запази snapshot“ за проследяване.",
  },
];

const STATUS_META = {
  done:        { label: "Готово",      icon: CheckCircle2, class: "text-emerald-600" },
  in_progress: { label: "В процес",    icon: Loader2,     class: "text-amber-600 animate-spin-slow" },
  pending:     { label: "Предстои",    icon: Circle,      class: "text-slate-400" },
};

const ACCENT_CLASSES = {
  emerald: {
    card:   "border-emerald-200 bg-emerald-50 dark:border-emerald-500/30 dark:bg-emerald-500/5",
    header: "text-emerald-700 dark:text-emerald-300",
    icon:   "text-emerald-600 dark:text-emerald-300",
  },
  blue: {
    card:   "border-blue-200 bg-blue-50 dark:border-blue-500/30 dark:bg-blue-500/5",
    header: "text-blue-700 dark:text-blue-300",
    icon:   "text-blue-600 dark:text-blue-300",
  },
  purple: {
    card:   "border-purple-200 bg-purple-50 dark:border-purple-500/30 dark:bg-purple-500/5",
    header: "text-purple-700 dark:text-purple-300",
    icon:   "text-purple-600 dark:text-purple-300",
  },
  pink: {
    card:   "border-pink-200 bg-pink-50 dark:border-pink-500/30 dark:bg-pink-500/5",
    header: "text-pink-700 dark:text-pink-300",
    icon:   "text-pink-600 dark:text-pink-300",
  },
  indigo: {
    card:   "border-indigo-200 bg-indigo-50 dark:border-indigo-500/30 dark:bg-indigo-500/5",
    header: "text-indigo-700 dark:text-indigo-300",
    icon:   "text-indigo-600 dark:text-indigo-300",
  },
  amber: {
    card:   "border-amber-200 bg-amber-50 dark:border-amber-500/30 dark:bg-amber-500/5",
    header: "text-amber-700 dark:text-amber-300",
    icon:   "text-amber-600 dark:text-amber-300",
  },
  green: {
    card:   "border-green-200 bg-green-50 dark:border-green-500/30 dark:bg-green-500/5",
    header: "text-green-700 dark:text-green-300",
    icon:   "text-green-600 dark:text-green-300",
  },
  teal: {
    card:   "border-teal-200 bg-teal-50 dark:border-teal-500/30 dark:bg-teal-500/5",
    header: "text-teal-700 dark:text-teal-300",
    icon:   "text-teal-600 dark:text-teal-300",
  },
  rose: {
    card:   "border-rose-200 bg-rose-50 dark:border-rose-500/30 dark:bg-rose-500/5",
    header: "text-rose-700 dark:text-rose-300",
    icon:   "text-rose-600 dark:text-rose-300",
  },
};

function StepCard({ step, index, progress }) {
  const a = ACCENT_CLASSES[step.accent] || ACCENT_CLASSES.blue;
  const Icon = step.icon;
  const status = progress?.status || null;
  const statusMeta = status ? STATUS_META[status] : null;
  const StatusIcon = statusMeta?.icon;
  return (
    <div
      className={`rounded-xl border p-4 ${a.card}`}
      data-testid={`gs-step-${index}`}
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {Icon ? <Icon className={`h-4 w-4 shrink-0 ${a.icon}`} /> : null}
          <div className={`text-xs font-bold uppercase tracking-wider ${a.header}`}>
            {step.day} · {step.label}
          </div>
        </div>
        {statusMeta && StatusIcon ? (
          <span
            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${statusMeta.class}`}
            data-testid={`gs-step-status-${step.id}`}
          >
            <StatusIcon className="h-3 w-3" />
            {statusMeta.label}
          </span>
        ) : null}
      </div>
      {progress?.detail_bg ? (
        <div className={`mb-2 text-[11px] font-medium ${statusMeta?.class || "text-slate-500"}`}>
          {progress.detail_bg}
        </div>
      ) : null}
      {typeof progress?.progress === "number" && progress.progress > 0 && progress.progress < 1 ? (
        <div className="mb-2 h-1 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
          <div
            className="h-full bg-purple-500 transition-all"
            style={{ width: `${Math.round(progress.progress * 100)}%` }}
          />
        </div>
      ) : null}
      <ol className="mt-1 list-inside list-decimal space-y-1 text-sm text-slate-700 dark:text-slate-200">
        {step.tasks.map((t, i) => <li key={i}>{t}</li>)}
      </ol>
      {step.note ? (
        <p className="mt-2 text-[11px] italic text-slate-500 dark:text-slate-400">{step.note}</p>
      ) : null}
      {step.result ? (
        <div className="mt-2 rounded-md bg-white/60 px-2 py-1 text-[11px] font-medium text-slate-700 dark:bg-slate-900/40 dark:text-slate-200">
          <span className="opacity-70">Резултат: </span>{step.result}
        </div>
      ) : null}
      {/* iter1042 — highlight banner (used on the "Labs" pivotal step) */}
      {step.highlight ? (
        <div className="mt-2 flex items-start gap-1.5 rounded-md border border-pink-300/60 bg-pink-50 px-2 py-1.5 text-[11px] font-medium text-pink-800 dark:border-pink-500/40 dark:bg-pink-500/10 dark:text-pink-200">
          <Sparkles className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>{step.highlight}</span>
        </div>
      ) : null}
      {/* iter1042 — biological pathways this step activates (patient education) */}
      {step.pathways_activated?.length ? (
        <div className="mt-2 rounded-md border border-purple-200/60 bg-purple-50/50 px-2 py-1.5 text-[11px] dark:border-purple-500/30 dark:bg-purple-500/5">
          <div className="mb-1 flex items-center gap-1 font-semibold text-purple-700 dark:text-purple-300">
            <Target className="h-3 w-3" />
            <span className="text-[10px] uppercase tracking-wider">Биологични пътища, които тази стъпка активира</span>
          </div>
          <ul className="ml-4 list-disc space-y-0.5 text-purple-800 dark:text-purple-200">
            {step.pathways_activated.map((p, i) => <li key={i}>{p}</li>)}
          </ul>
        </div>
      ) : null}
      {step.warning ? (
        <div className="mt-2 flex items-start gap-1.5 rounded-md border border-amber-300/60 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>{step.warning}</span>
        </div>
      ) : null}
    </div>
  );
}

/** Renders the guide inline (no modal chrome). Used on the methodology page. */
export function BhiGettingStartedInline({ testId = "getting-started-inline", withProgress = false }) {
  const { API, user } = useAuth();
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    if (!withProgress || !user) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/breast-intelligence/onboarding-progress`);
        if (alive) setProgress(data);
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, [API, user, withProgress]);

  const stepMap = (progress?.steps || []).reduce((acc, s) => { acc[s.id] = s; return acc; }, {});

  return (
    <div data-testid={testId} className="space-y-3">
      {progress ? (
        <div
          className="mb-2 rounded-lg border border-slate-200 bg-slate-50 p-3 dark:border-white/10 dark:bg-slate-900/40"
          data-testid="gs-progress-summary"
        >
          <div className="mb-1 flex items-center justify-between text-xs">
            <span className="font-semibold text-slate-700 dark:text-slate-200">
              Твоят прогрес: {progress.done}/{progress.total} стъпки
            </span>
            <span className="text-slate-500">{progress.percent}%</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-800">
            <div
              className="h-full rounded-full bg-gradient-to-r from-pink-500 to-purple-500 transition-all"
              style={{ width: `${progress.percent}%` }}
            />
          </div>
        </div>
      ) : null}

      <p className="text-sm text-slate-700 dark:text-slate-300">
        Модулът показва тежестта на които може да променим биологични сигнали. Затова има смисъл да има данни за анализ. Ето минималния път за 30 дни:
      </p>

      {/* iter1042 — Prevention narrative: why the steps matter biologically */}
      <div className="rounded-lg border border-purple-200/70 bg-gradient-to-br from-purple-50 to-pink-50 p-3 text-xs text-slate-800 dark:border-purple-500/30 dark:from-purple-500/10 dark:to-pink-500/5 dark:text-slate-200"
           data-testid="gs-prevention-narrative">
        <div className="mb-1 flex items-center gap-1.5 font-bold text-purple-800 dark:text-purple-200">
          <Target className="h-3.5 w-3.5" />
          <span>Защо всяка стъпка има значение?</span>
        </div>
        <p className="leading-relaxed">
          Всяка попълнена стъпка <strong>активира конкретни биологични пътища</strong> (KEGG pathways),
          които са в основата на превенцията на заболяванията на гърдата —
          <em> естроген каскада, инсулин/mTOR, NF-κB възпаление, естроболом, DNA repair, детоксикация</em>.
          Колкото повече данни имаме, толкова по-точно виждаме кои пътища носят най-голям товар за
          <strong> твоя</strong> профил и генерираме персонализирани, а не общи препоръки.
        </p>
      </div>
      <div className="space-y-3">
        {STEPS.map((s, i) => <StepCard key={i} step={s} index={i} progress={stepMap[s.id]} />)}
      </div>
      <p className="text-xs text-slate-500 dark:text-slate-400">
        <strong>Кратко правило:</strong> На всеки етап при липса на данни модулът връща „Данни в очакване“ с actionable checklist — никакви фалшиви стойности.
      </p>
    </div>
  );
}

/** Modal wrapper triggered by the "🧭 Откъде да започна" button on the BHI page. */
export default function BhiGettingStartedModal({ open, onClose, isDark = false }) {
  useEffect(() => {
    if (!open) return;
    const onEsc = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-start justify-center overflow-y-auto bg-slate-900/60 p-4 backdrop-blur-sm"
      onClick={onClose}
      data-testid="gs-modal-overlay"
    >
      <div
        className={`my-8 w-full max-w-2xl rounded-2xl border shadow-2xl ${isDark ? "border-white/10 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-800"}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        data-testid="gs-modal"
      >
        <header className="sticky top-0 z-10 flex items-center justify-between gap-3 rounded-t-2xl border-b border-slate-200 bg-inherit px-5 py-3 dark:border-white/10">
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-pink-500" />
            <h2 className="text-lg font-bold">Откъде да започна</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="gs-modal-close"
            className={`inline-flex items-center justify-center rounded-md p-1.5 transition ${isDark ? "text-slate-400 hover:bg-white/10 hover:text-white" : "text-slate-500 hover:bg-slate-100 hover:text-slate-800"}`}
            aria-label="Затвори"
          >
            <X className="h-5 w-5" />
          </button>
        </header>
        <div className="p-5">
          <BhiGettingStartedInline testId="gs-modal-content" withProgress={true} />
        </div>
      </div>
    </div>
  );
}
